import React, { useRef, useState, useEffect } from 'react';
import { CtaButton } from '../Buttons';

/**
 * SelectField Component
 *
 * A flexible select field component that can render as either a native HTML select
 * or as a button with a custom dropdown menu.
 *
 * @param {boolean} asButton - When true, renders a CtaButton instead of native select with custom dropdown
 * @param {object} buttonConfig - Configuration object for CtaButton component (only used when asButton=true)
 *   - variant: Button variant ('primaryLight', 'primaryDark', 'secondary', etc.)
 *   - size: Button size ('sm', 'md', 'lg')
 *   - Other CtaButton props as needed
 *
 * Usage Example (as button):
 * ```
 * <SelectField
 *   asButton={true}
 *   buttonConfig={{ variant: 'secondary', size: 'md' }}
 *   labelConfig={{ inputID: 'mySelect', labelTxt: 'Choose Option' }}
 *   options={[
 *     { id: '1', value: 'Option 1' },
 *     { id: '2', value: 'Option 2' },
 *     { id: '3', value: 'Option 3' }
 *   ]}
 *   extraAttri={{
 *     value: selectedValue,
 *     onChange: (e) => setSelectedValue(e.target.value)
 *   }}
 * />
 * ```
 *
 * Usage Example (as native select):
 * ```
 * <SelectField
 *   asButton={false}
 *   labelConfig={{ inputID: 'mySelect', labelTxt: 'Choose Option' }}
 *   options={[
 *     { id: '1', value: 'Option 1' },
 *     { id: '2', value: 'Option 2' }
 *   ]}
 *   extraAttri={{
 *     value: selectedValue,
 *     onChange: (e) => setSelectedValue(e.target.value)
 *   }}
 * />
 * ```
 */
const SelectField = ({
  inputRef,
  parentExtraCls,
  inputValidate,
  asButton = false,
  extraAttri = {},
  hiddenlabel = false,
  labelConfig = {},
  validationConfig = {},
  options = [],
  registerFunction,
  isDisabled,
  buttonConfig = {}
}) => {
  const { ref = null, ...rest } = registerFunction ? registerFunction(labelConfig.inputID) : {};
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(extraAttri?.value || options[0]?.id || '');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!asButton) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, asButton]);

  // Update selected value when extraAttri.value changes
  useEffect(() => {
    if (extraAttri?.value !== undefined) {
      setSelectedValue(extraAttri.value);
    }
  }, [extraAttri?.value]);

  const handleButtonClick = () => {
    if (!isDisabled) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleOptionClick = (optionId) => {
    setSelectedValue(optionId);
    setIsDropdownOpen(false);

    // Trigger onChange if provided
    if (extraAttri?.onChange) {
      const event = {
        target: { value: optionId, name: extraAttri?.name || labelConfig?.inputID }
      };
      extraAttri.onChange(event);
    }

    // If using react-hook-form, trigger the change
    if (rest?.onChange) {
      const event = {
        target: { value: optionId, name: extraAttri?.name || labelConfig?.inputID }
      };
      rest.onChange(event);
    }
  };

  const getSelectedLabel = () => {
    const selected = options.find((opt) => opt?.id === selectedValue || opt?.id === String(selectedValue));
    return selected?.value || options[0]?.value || 'Select';
  };

  if (asButton) {
    return (
      <>
        <div
          className={`si-input si-input--dropdown ${parentExtraCls ? parentExtraCls : ''} ${
            inputValidate ? 'si-invalid' : ''
          } ${isDisabled ? 'si-disabled' : ''}`}
          ref={dropdownRef}
        >
          {!hiddenlabel && labelConfig?.labelTxt && (
            <label className="si-lbl" htmlFor={labelConfig.inputID}>
              {labelConfig.labelTxt}
            </label>
          )}
          <div className='si-select__field si-select__field--button'>
            <CtaButton
              onClick={handleButtonClick}
              disabled={isDisabled}
              variant={buttonConfig?.variant || 'secondary'}
              icon={isDropdownOpen ? 'f1i-chevron-up' : 'f1i-chevron-down'}
              {...buttonConfig}
            >
              {getSelectedLabel()}
            </CtaButton>

            {isDropdownOpen && (
              <div className='si-select__dropdown'>
                <ul className='si-select__options'>
                  {options.map((option, index) => (
                    <li
                      key={index}
                      className={`si-select__option ${
                        selectedValue === option?.id || String(selectedValue) === String(option?.id) ? 'si-select__option--selected' : ''
                      }`}
                      onClick={() => handleOptionClick(option?.id)}
                    >
                      {option?.value}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Hidden select for form compatibility */}
            <select
              ref={ref || inputRef}
              {...rest}
              {...extraAttri}
              value={selectedValue}
              style={{ display: 'none' }}
              onChange={(e) => {
                setSelectedValue(e.target.value);
                if (extraAttri?.onChange) extraAttri.onChange(e);
              }}
            >
              {options.map((option, index) => (
                <option key={index} value={option?.id}>
                  {option?.value}
                </option>
              ))}
            </select>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className={`si-input  ${parentExtraCls ? parentExtraCls : ''} ${
          inputValidate ? 'si-invalid' : ''
        } ${isDisabled ? 'si-disabled' : ''}`}
      >
        {!hiddenlabel && labelConfig?.labelTxt && (
          <label className="si-lbl" htmlFor={labelConfig.inputID}>
            {labelConfig.labelTxt}
          </label>
        )}
        <div className='si-select__field'>
          <select ref={ref || inputRef} {...rest} {...extraAttri}>
            {options.map((option, index) => (
              <option key={index} value={option?.id}>
                {option?.value}
              </option>
            ))}
          </select>
        </div>
      </div>
    </>
  );
};

export default SelectField;

